import {useAppForm, focusFirstErrorField} from '@cypro/ui/integrations/form'
import {isDefined} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {type SubmitEvent, useCallback} from 'react'
import {StabilityRuleFormOptions} from '../constants'
import {getNewFormStabilityRules} from '../functions'
import {
  closeStabilityRuleFormDialog,
  openStabilityRuleFormDialog,
} from '../stability-rules/components/stability-rule-form/util'
import {getStabilityRuleFormDefaultValues} from '../stability-rules/functions'
import {StabilityRuleFormSchema} from '../stability-rules/schemas'

interface UseStabilityRuleFormOptions {
  /** Reads the stability rules from the owning form (usually the medication profile form). */
  getStabilityRules: () => StabilityRuleFormSchema[]
  /** Writes the stability rules back to the owning form. */
  setStabilityRules: (rules: StabilityRuleFormSchema[]) => void
}

export function useStabilityRuleForm({getStabilityRules, setStabilityRules}: UseStabilityRuleFormOptions) {
  const {t} = useTranslate('common')

  const stabilityRuleForm = useAppForm({
    ...StabilityRuleFormOptions,
    onSubmit({value, formApi, meta}) {
      const stabilityRules = getStabilityRules()
      const newStabilityRules = getNewFormStabilityRules(stabilityRules, value, meta.index)
      setStabilityRules(newStabilityRules)
      formApi.reset()
      closeStabilityRuleFormDialog()
    },
    onSubmitInvalid() {
      focusFirstErrorField()
    },
  })

  const onAddStabilityRule = useCallback(
    async (index?: number) => {
      function onSubmit(event: SubmitEvent) {
        event.preventDefault()
        stabilityRuleForm.handleSubmit({index: isDefined(index) ? index : -1})
      }

      await openStabilityRuleFormDialog({
        title: t('add_stability_rule_dialog.title'),
        description: t('add_stability_rule_dialog.description'),
        form: stabilityRuleForm,
        onSubmit,
      })

      stabilityRuleForm.reset(getStabilityRuleFormDefaultValues(undefined))
    },
    [t, stabilityRuleForm]
  )

  const onEditStabilityRule = useCallback(
    (rule: StabilityRuleFormSchema, index: number) => {
      stabilityRuleForm.reset(rule)
      onAddStabilityRule(index)
    },
    [stabilityRuleForm, onAddStabilityRule]
  )

  return {stabilityRuleForm, onAddStabilityRule, onEditStabilityRule}
}
